import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import type { UserLicensesDetails } from "../user/types/user";
import { LicensesResponse } from "./types/license.api";

export class LicenseState {
    private eventBus: EventBus;

    private userLicensesDetails: UserLicensesDetails | null = null;

    constructor(eventBus: EventBus) {
        this.eventBus = eventBus;
    }

    public get details(): UserLicensesDetails | null {
        return this.userLicensesDetails;
    }

    public set(details: UserLicensesDetails | null): void {
        this.userLicensesDetails = details;
    }

    /**
     * @method seedFromBootstrap
     * Alimente le cache des licences à partir des données bootstrap, sans appel API supplémentaire.
     * Doit être appelé par UserProfileLoader._bootstrapUserProfile() dès réception de la réponse bootstrap.
     * ═══════════════════════════════════════════════════════════════════════════
     * @param licenses          - LicensesResponse issu de BootstrapUserResponseDTO
     * @param monthlySpentTokenCount - Tokens dépensés ce mois (userProfile.monthlySpentTokenCount)
     */
    public seedFromBootstrap(
        licenses: LicensesResponse,
        monthlySpentTokenCount: number,
    ): void {
        logger.info(LOGS.LicenseManager, "seedFromBootstrap()");

        this.userLicensesDetails = {
            monthlySpentTokenCount: monthlySpentTokenCount,
            monthlyTokenCount:
                licenses.active[0]?.detail.monthlyLimit.byToken ?? 0,
            active: licenses.active,
            expired: licenses.expired,
            upcoming: licenses.upcoming,
        };

        this.eventBus.emit("user-license-updated", this.hasValidLicense());
    }

    /**
     * @method hasValidLicense
     * Vérifie si l'utilisateur possède une licence valide
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {boolean} True si l'utilisateur a une licence valide
     */
    public hasValidLicense = (): boolean => {
        if (!this.userLicensesDetails) {
            logger.info(
                LOGS.LicenseManager,
                "Full licence details were not retrieved or are empty",
            );
            return false;
        }

        const hasActiveLicense = this.userLicensesDetails.active.length > 0;

        if (!hasActiveLicense) {
            logger.info(LOGS.LicenseManager, "User has no active license");
            return false;
        }

        logger.info(
            LOGS.LicenseManager,
            `${this.userLicensesDetails.active.length} active license(s) found for current user`,
        );
        return true;
    };
}
